import { ApplicationStore, VisualElementRef } from "../../../store/ApplicationStore";
import {CompositionService} from "../../../services/CompositionService";
import { ScriptLayerClip } from "../../../../engine/clips/script/script";
import { ContextMenuStore } from "../../../store/ContextMenuStore";
import { COMPOSITION_CONTAINER_ID } from "../../../config/config";

export class SelectionWatcher {
    private static instance: SelectionWatcher | null = null;
    private element: HTMLCanvasElement | null = null;
    private container: HTMLElement | null = null;
    private pointerDown: { x: number; y: number } | null = null;
    private pointerMoved = false;
    private static readonly DRAG_THRESHOLD_PX = 4;

    public static getInstance(): SelectionWatcher {
        if (!SelectionWatcher.instance) {
            SelectionWatcher.instance = new SelectionWatcher();
        }
        return SelectionWatcher.instance;
    }

    public init(element: HTMLCanvasElement) {
        if (this.container) {
            this.container.removeEventListener("pointerdown", this.onPointerDown, true);
            this.container.removeEventListener("pointermove", this.onPointerMove, true);
            this.container.removeEventListener("pointerup", this.onPointerUp, true);
            this.container.removeEventListener("contextmenu", this.onContextMenu, true);
            this.container.removeEventListener("dblclick", this.onDoubleClick, true);
        }

        this.element = element;
        this.container = document.getElementById(COMPOSITION_CONTAINER_ID) as HTMLElement | null;

        // Listen on the composition container so clicks work even when the Moveable overlay
        // is on top of the canvas (e.g. selecting captions over a previously selected clip).
        if (this.container) {
            this.container.addEventListener("pointerdown", this.onPointerDown, true);
            this.container.addEventListener("pointermove", this.onPointerMove, true);
            this.container.addEventListener("pointerup", this.onPointerUp, true);
            // Use capture phase to ensure we get the contextmenu event before any parent handlers
            this.container.addEventListener("contextmenu", this.onContextMenu, true);
            // Double-click to enter text editing for TextClips
            this.container.addEventListener("dblclick", this.onDoubleClick, true);
        }
    }

    public destroy() {
        if (this.container) {
            this.container.removeEventListener("pointerdown", this.onPointerDown, true);
            this.container.removeEventListener("pointermove", this.onPointerMove, true);
            this.container.removeEventListener("pointerup", this.onPointerUp, true);
            this.container.removeEventListener("contextmenu", this.onContextMenu, true);
            this.container.removeEventListener("dblclick", this.onDoubleClick, true);
        }
        this.container = null;
        SelectionWatcher.instance = null; // Allow garbage collection
    }

    private getPositionFromEvent(event: MouseEvent): [number, number] {
        if (!this.element) return [0, 0];

        // Use clientX/Y + bounding rect so this works for events whose target is NOT the canvas
        // (e.g. Moveable overlays) and correctly accounts for CSS transforms (scale).
        const canvasRect = this.element.getBoundingClientRect();
        const scaleX = canvasRect.width / this.element.width;
        const scaleY = canvasRect.height / this.element.height;
        const x = (event.clientX - canvasRect.left) / (scaleX || 1);
        const y = (event.clientY - canvasRect.top) / (scaleY || 1);
        return [x, y];
    }

    private onPointerDown = (event: PointerEvent) => {
        // Only use left-click for selection.
        if (event.button !== 0) return;
        if (ApplicationStore.isCropMode) return;
        this.pointerDown = { x: event.clientX, y: event.clientY };
        this.pointerMoved = false;
    };

    private onPointerMove = (event: PointerEvent) => {
        if (!this.pointerDown) return;
        const dx = event.clientX - this.pointerDown.x;
        const dy = event.clientY - this.pointerDown.y;
        const dist2 = dx * dx + dy * dy;
        if (dist2 > SelectionWatcher.DRAG_THRESHOLD_PX * SelectionWatcher.DRAG_THRESHOLD_PX) {
            this.pointerMoved = true;
        }
    };

    /**
     * Check if a point is within bounds
     */
    private isPointInBounds(x: number, y: number, bounds: { x: number; y: number; width: number; height: number }): boolean {
        const x0 = bounds.x ?? 0;
        const y0 = bounds.y ?? 0;
        const x1 = x0 + (bounds.width ?? 0);
        const y1 = y0 + (bounds.height ?? 0);
        return x >= x0 && x <= x1 && y >= y0 && y <= y1;
    }

    private onPointerUp = (event: PointerEvent) => {
        if (!this.pointerDown || !this.element) return;
        if (ApplicationStore.isCropMode) {
            this.pointerDown = null;
            this.pointerMoved = false;
            return;
        }

        const isClick = !this.pointerMoved;
        this.pointerDown = null;
        this.pointerMoved = false;
        if (!isClick) return;

        const rect = this.element.getBoundingClientRect();
        const inCanvas =
            event.clientX >= rect.left &&
            event.clientX <= rect.right &&
            event.clientY >= rect.top &&
            event.clientY <= rect.bottom;
        if (!inCanvas) {
            return;
        }

        const [cursorX, cursorY] = this.getPositionFromEvent(event);
        try {
            const hitRef = this.hitTestVisualElement(cursorX, cursorY);
            if (hitRef) {
                // Use setSelectedVisualElement for canvas clicks (includes sourceIndex for ScriptLayerClip)
                ApplicationStore.setSelectedVisualElement(hitRef);
            } else {
                ApplicationStore.clearSelectedClipId();
            }
        } catch (e) {
            console.warn('[SelectionWatcher] Error during hit test:', e);
        }
    };
    
    /**
     * Handle right-click context menu on canvas (private handler for canvas events)
     */
    private onContextMenu = (event: MouseEvent) => {
        this.handleContextMenu(event);
    };

    private onDoubleClick = (event: MouseEvent) => {
        // Only act on left-button double click.
        if ((event as any).button != null && (event as any).button !== 0) return;
        if (ApplicationStore.isCropMode) return;
        if (!this.element) return;

        const [cursorX, cursorY] = this.getPositionFromEvent(event);
        try {
            const hitRef = this.hitTestVisualElement(cursorX, cursorY);
            if (!hitRef) return;
            const clip: any = CompositionService.getClipById(hitRef.clipId);
            if (!clip) return;
            if (String(clip.type) !== "text") return;

            // Ensure selection and enter editing mode.
            ApplicationStore.setSelectedVisualElement(hitRef);
            ApplicationStore.startEditingTextClip(hitRef.clipId);
        } catch (e) {
            console.warn("[SelectionWatcher] Error during dblclick handler:", e);
        }
    };
    
    /**
     * Public method to handle context menu - can be called from parent container
     */
    public handleContextMenu(event: MouseEvent): void {
        // Always prevent default browser context menu on canvas
        event.preventDefault();
        event.stopPropagation();
        
        if (!this.element) return;
        
        const [cursorX, cursorY] = this.getPositionFromEvent(event);
        
        try {
            const hitRef = this.hitTestVisualElement(cursorX, cursorY);
            
            if (hitRef) {
                // Select the element
                ApplicationStore.setSelectedVisualElement(hitRef);
                
                // Build context menu options
                const menuOptions = this.buildCanvasContextMenu(hitRef);
                
                // Show context menu at mouse position
                ContextMenuStore.show(event.clientX, event.clientY, menuOptions);
            }
        } catch (e) {
            console.warn('[SelectionWatcher] Error during context menu:', e);
        }
    }
    
    /**
     * Build context menu options for a visual element on canvas
     */
    private buildCanvasContextMenu(hitRef: VisualElementRef): Array<{ title: string; onClick?: () => void; children?: Array<{ title: string; onClick: () => void }> }> {
        const { clipId, sourceIndex } = hitRef;
        const clip: any = CompositionService.getClipById(clipId);
        const isScriptClip = clip?.type === 'script';
        
        const menuOptions: Array<{ title: string; onClick?: () => void; children?: Array<{ title: string; onClick: () => void }> }> = [];

        // Placeholder: replace source via the Project sidebar (no inline picker).
        if (clip?.type === "placeholder") {
            menuOptions.push({
                title: "Add Media",
                onClick: () => {
                    try {
                        ApplicationStore.setPendingPlaceholderReplaceContext({ placeholderClipId: clipId });
                    } catch {}
                    try {
                        window.dispatchEvent(new CustomEvent("openProjectSidebarFiles", { detail: { source: "placeholder" } }));
                    } catch {}
                    ContextMenuStore.hide();
                },
            });
        }
        
        // Layer Order submenu
        menuOptions.push({
            title: "Layer order",
            children: [
                { 
                    title: "Bring to front", 
                    onClick: () => { 
                        CompositionService.bringVisualToFront(clipId, sourceIndex); 
                        ContextMenuStore.hide(); 
                    } 
                },
                { 
                    title: "Bring forward", 
                    onClick: () => { 
                        CompositionService.bringVisualForward(clipId, sourceIndex); 
                        ContextMenuStore.hide(); 
                    } 
                },
                { 
                    title: "Send backward", 
                    onClick: () => { 
                        CompositionService.sendVisualBackward(clipId, sourceIndex); 
                        ContextMenuStore.hide(); 
                    } 
                },
                { 
                    title: "Send to back", 
                    onClick: () => { 
                        CompositionService.sendVisualToBack(clipId, sourceIndex); 
                        ContextMenuStore.hide(); 
                    } 
                },
            ],
        });
        
        // Delete Layer
        menuOptions.push({
            title: isScriptClip && sourceIndex !== undefined ? "Delete visual" : "Delete layer",
            onClick: () => {
                CompositionService.deleteVisualElement(clipId, sourceIndex);
                ContextMenuStore.hide();
            },
        });
        
        return menuOptions;
    }
    
    /**
     * Hit test to find the topmost visual element under the cursor
     */
    private hitTestVisualElement(cursorX: number, cursorY: number): VisualElementRef | null {
        const comp: any = (CompositionService as any)._composition || CompositionService.composition;
        if (!comp || !Array.isArray(comp.tracks)) return null;

        // Match the same global visual z domain used by CompositionService._rebuildStageOrder().
        const TRACK_Z_STEP = 1000;
        const trackZ = (trackIndex: number): number => -trackIndex * TRACK_Z_STEP;

        const tracks: any[] = comp.tracks ?? [];
        const scriptTrackId = comp?.scriptLayer?.layerId ?? null;
        const scriptTrackIndex =
            typeof scriptTrackId === "string" && scriptTrackId
                ? tracks.findIndex((t) => t?.id === scriptTrackId)
                : -1;
        const scriptBaseZ = scriptTrackIndex >= 0 ? trackZ(scriptTrackIndex) : trackZ(tracks.length);

        const candidates: Array<{ z: number; ref: VisualElementRef }> = [];

        for (let ti = 0; ti < tracks.length; ti++) {
            const track = tracks[ti];
            if (!track || !Array.isArray(track.clips)) continue;

            for (const clip of track.clips) {
                if (!clip) continue;

                // Script layer sources (direct stage children; ordered by per-source layerIndex)
                if (clip instanceof ScriptLayerClip) {
                    const entries = typeof clip.getVideoEntries === "function" ? clip.getVideoEntries() : [];
                    for (let sourceIndex = 0; sourceIndex < entries.length; sourceIndex++) {
                        const sprite = clip.getVideoSprite(sourceIndex);
                        if (!sprite || !(sprite as any).parent) continue;
                        const bounds = typeof (sprite as any).getBounds === "function" ? (sprite as any).getBounds() : null;
                        if (!bounds) continue;
                        if (!this.isPointInBounds(cursorX, cursorY, bounds)) continue;

                        const li = Number(entries[sourceIndex]?.layerIndex ?? 0);
                        const layerIndex = Number.isFinite(li) ? li : 0;
                        candidates.push({
                            z: scriptBaseZ + layerIndex,
                            ref: { clipId: clip.id, sourceIndex },
                        });
                    }
                    continue;
                }

                // Regular visual clips currently in graph.
                if (
                    !clip?.view ||
                    (clip.type !== "video" &&
                        clip.type !== "image" &&
                        clip.type !== "captions" &&
                        clip.type !== "text" &&
                        clip.type !== "placeholder")
                )
                    continue;
                if (!(clip.view as any).parent) continue;
                const bounds = typeof clip.view.getBounds === "function" ? clip.view.getBounds() : null;
                if (!bounds) continue;
                if (!this.isPointInBounds(cursorX, cursorY, bounds)) continue;
                candidates.push({ z: trackZ(ti), ref: { clipId: clip.id } });
            }
        }

        if (candidates.length === 0) return null;
        candidates.sort((a, b) => b.z - a.z);
        return candidates[0].ref ?? null;
    }
}
